(() => {
  if (customElements.get('collection-nav')) return;

  // Share in-flight results between sections; only three small section requests at once.
  const images = new Map();
  const queue = [];
  let pending = 0;
  const drain = () => {
    while (pending < 3 && queue.length) {
      pending++;
      const job = queue.shift();
      job().finally(() => { pending--; drain(); });
    }
  };
  const bestseller = url => {
    if (!images.has(url)) {
      images.set(url, new Promise(resolve => {
        queue.push(async () => {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 8000);
          try {
            const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
            if (!response.ok) throw new Error('Collection image unavailable');
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            resolve(doc.querySelector('[data-cnav-bestseller] img'));
          } catch (_) {
            images.delete(url);
            resolve(null);
          } finally {
            clearTimeout(timeout);
          }
        });
      }));
      drain();
    }
    return images.get(url);
  };

  class CollectionNav extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.cnav__track');
      if (!this.track) return;
      this.controller?.abort();
      const { signal } = this.controller = new AbortController();
      const track = this.track;
      const scroll = direction => track.scrollBy({
        left: direction * Math.max(1, track.clientWidth * .75),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        scroll(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });

      let drag = null;
      let dragged = false;
      track.addEventListener('pointerdown', event => {
        dragged = false;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = { x: event.clientX, left: track.scrollLeft };
      }, { signal });
      track.addEventListener('pointermove', event => {
        if (!drag) return;
        const distance = event.clientX - drag.x;
        if (!dragged && Math.abs(distance) < 6) return;
        dragged = true;
        event.preventDefault();
        track.classList.add('is-dragging');
        track.scrollLeft = drag.left - distance;
      }, { signal });
      const stopDrag = () => { drag = null; track.classList.remove('is-dragging'); };
      window.addEventListener('pointerup', stopDrag, { signal });
      track.addEventListener('pointercancel', stopDrag, { signal });
      track.addEventListener('pointerleave', stopDrag, { signal });
      track.addEventListener('dragstart', event => event.preventDefault(), { signal });
      track.addEventListener('click', event => {
        if (dragged && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); }
      }, { capture: true, signal });
      track.addEventListener('click', event => {
        const item = event.target.closest('[data-cnav-collection]');
        if (!item || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || item.target === '_blank') return;
        const url = new URL(item.href);
        // Only collection pages have a grid to replace; homepage cards follow their native links.
        if (url.origin !== location.origin || !this.sections().some(section => section.querySelector('[data-cnav-page-state]'))) return;
        event.preventDefault();
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        this.navigate(url, item);
      }, { signal });
      this.renderedURL = location.href;
      window.addEventListener('popstate', () => {
        if (this !== document.querySelector('collection-nav') || this.renderedURL === location.href) return;
        const url = new URL(location.href);
        const item = [...track.querySelectorAll('[data-cnav-collection]')].find(link => new URL(link.href).pathname === url.pathname);
        this.navigate(url, item, false);
      }, { signal });
      document.addEventListener('facet:update', () => {
        this.navigation?.abort();
        this.setLoading(null);
      }, { signal });
      document.addEventListener('facet:content-replaced', () => { this.renderedURL = location.href; }, { signal });

      const active = track.querySelector('[aria-current="page"]');
      if (active) {
        const inset = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        track.scrollLeft = active.offsetLeft - inset;
      }
      this.observer?.disconnect();
      this.observer = new IntersectionObserver(entries => {
        entries.filter(entry => entry.isIntersecting).forEach(({ target }) => {
          this.observer.unobserve(target);
          this.loadImage(target, signal);
        });
      }, { root: track, rootMargin: '0px 260px', threshold: 0 });
      track.querySelectorAll('[data-cnav-collection]').forEach(item => this.observer.observe(item));
      document.addEventListener('shopify:block:select', event => {
        const item = event.target.closest('.cnav__item');
        if (item && this.contains(item)) track.scrollLeft = item.offsetLeft - 20;
      }, { signal });
    }

    sections() {
      // Re-render the current collection view in the destination collection context.
      // Header, cart, footer and this navigation remain mounted.
      return [...document.querySelectorAll('#main > .shopify-section[id^="shopify-section-template--"]')]
        .filter(section => !section.querySelector('collection-nav'));
    }

    setLoading(item) {
      this.classList.toggle('is-navigating', Boolean(item));
      this.track.querySelectorAll('.cnav__item').forEach(card => {
        card.classList.toggle('is-loading', card === item);
        if (card === item) card.setAttribute('aria-busy', 'true');
        else card.removeAttribute('aria-busy');
      });
      this.querySelector('[data-cnav-status]').textContent = item ? `Loading ${item.querySelector('.cnav__title').textContent}…` : '';
    }

    async navigate(url, item, push = true) {
      this.navigation?.abort();
      const request = this.navigation = new AbortController();
      const timeout = setTimeout(() => request.abort('timeout'), 10000);
      const targets = this.sections();
      const main = targets.find(section => section.querySelector('[data-cnav-page-state]'));
      this.setLoading(item);
      if (main) main.setAttribute('aria-busy', 'true');
      document.dispatchEvent(new CustomEvent('collection:navigation-start'));
      try {
        if (!main) throw new Error('Collection section unavailable');
        const ids = targets.map(section => section.id.replace('shopify-section-', ''));
        const batches = [];
        for (let index = 0; index < ids.length; index += 5) batches.push(ids.slice(index, index + 5));
        const responses = await Promise.all(batches.map(async batch => {
          const endpoint = new URL(url);
          endpoint.searchParams.set('sections', batch.join(','));
          const response = await fetch(endpoint.href, { signal: request.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Collection unavailable');
          return response.json();
        }));
        const html = Object.assign({}, ...responses);
        // Validate every section before changing any content or the URL.
        const replacements = targets.map((target, index) => {
          if (typeof html[ids[index]] !== 'string') throw new Error('Missing collection section');
          const doc = new DOMParser().parseFromString(html[ids[index]], 'text/html');
          const replacement = doc.getElementById(target.id);
          if (!replacement) throw new Error('Invalid collection section');
          return { target, replacement };
        });
        const state = replacements.find(entry => entry.target === main)?.replacement.querySelector('[data-cnav-page-state]');
        if (!state || state.dataset.handle !== decodeURIComponent(url.pathname.split('/').filter(Boolean).pop())) throw new Error('Unexpected collection');
        if (request.signal.aborted || !this.isConnected) return;
        if (push) history.pushState({ ...history.state, collectionNav: true }, '', url.href);
        this.renderedURL = url.href;
        replacements.forEach(({ target, replacement }) => {
          target._infiniteScrollObserver?.disconnect();
          target.querySelectorAll('[id^="kopstorm-"]').forEach(node => node.cnavCleanup?.());
          target.replaceChildren(...document.importNode(replacement, true).childNodes);
          // Only this theme-owned initializer needs explicit execution; components
          // connect automatically and product-grid behavior uses the facet event.
          target.querySelectorAll('script[data-collection-title-init]').forEach(inert => {
            const script = document.createElement('script');
            script.textContent = inert.textContent;
            inert.replaceWith(script);
          });
        });
        document.title = state.dataset.title;
        document.querySelector('link[rel=canonical]')?.setAttribute('href', state.dataset.canonical);
        let description = document.querySelector('meta[name=description]');
        if (!description) { description = document.createElement('meta'); description.name = 'description'; document.head.append(description); }
        description.content = state.dataset.description;
        this.track.querySelectorAll('[data-cnav-collection]').forEach(card => {
          const active = new URL(card.href).pathname === url.pathname;
          card.classList.toggle('cnav__item--active', active);
          if (active) card.setAttribute('aria-current', 'page');
          else card.removeAttribute('aria-current');
        });
        document.dispatchEvent(new CustomEvent('facet:content-replaced', { detail: { sectionId: main.id.replace('shopify-section-', '') } }));
        document.dispatchEvent(new CustomEvent('collection:changed', { detail: { url: url.href, handle: state.dataset.handle } }));
      } catch (_) {
        // Keep native links as a recovery path for unavailable sections or networks.
        if (!request.signal.aborted || request.signal.reason === 'timeout') location.assign(url.href);
      } finally {
        clearTimeout(timeout);
        if (this.navigation === request) {
          main?.removeAttribute('aria-busy');
          this.setLoading(null);
        }
      }
    }

    async loadImage(item, signal) {
      // A custom image picked in the block is rendered by the section and stays as it is
      if (item.hasAttribute('data-cnav-custom')) return;
      const url = new URL(item.dataset.cnavCollection, location.origin);
      if (url.origin !== location.origin) return;
      url.search = '';
      url.searchParams.set('sort_by', 'best-selling');
      url.searchParams.set('section_id', 'collection-nav-image');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const image = await bestseller(url.href);
      if (!image || signal.aborted) return;
      const replacement = new Image();
      ['srcset', 'sizes', 'width', 'height'].forEach(name => {
        if (image.hasAttribute(name)) replacement.setAttribute(name, image.getAttribute(name));
      });
      replacement.alt = '';
      replacement.draggable = false;
      replacement.decoding = 'async';
      replacement.onload = async () => {
        try { await replacement.decode(); } catch (_) { return; }
        if (!signal.aborted) item.querySelector('[data-cnav-media]').replaceChildren(replacement);
      };
      replacement.src = image.getAttribute('src');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.navigation?.abort();
      this.observer?.disconnect();
    }
  }
  customElements.define('collection-nav', CollectionNav);
})();
